import { FooterContainer, FooterActionsContainer } from '../styles'
import { useSearchParams } from 'react-router-dom'
import ActionButton from 'components/ActionButton'
import StudentTools from '../../../components/StudentTools/StudentTools'
import { useAppSelector } from 'app/hooks'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
import { useContext } from 'react'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import { selectCurrentClassroom } from 'app/reducer/studentSlice'
import Cookies from 'js-cookie'
import { useFormulaSheet } from 'hooks/useFormulaSheet'
import OpenLink from 'assets/img/link-icon.png'

type AssignmentsFooterProps = {
  type: 'assignments'
  onNext: () => void
  disableNext: boolean
  onBack: () => void
  disableBack: boolean

  onClear: () => void
  disableClear: boolean

  onPauseExit?: () => void
  disablePauseExit?: boolean

  onFinish: () => void
  disableFinish: boolean
}

type AssessmentsFooterProps = {
  type: 'assessments'
  onClear: () => void
  onPauseExit: () => void
  onFinish: () => void
  readOnly?: boolean
  disableFinish: boolean
  disableClear: boolean
  onNext: () => void
  onBack: () => void
  disableNext: boolean
  disableBack: boolean
}

type PreviewFooterProps = {
  type: 'preview-assignment' | 'preview-assessment'
  onNext: () => void
  readOnly: boolean
  onBack: () => void
  disableStudentView?: boolean
  disableNext: boolean
  disableBack: boolean
}

type FooterProps =
  | AssignmentsFooterProps
  | AssessmentsFooterProps
  | PreviewFooterProps

const Footer = (props: FooterProps) => {
  const [searchParams, setLocalSearchParams] = useSearchParams()
  const showAnswers = searchParams.get('showAnswers') !== 'false'
  const { enableStudentSubmissionView, other_tools } = useAppSelector(
    SelectAssignmentState
  )
  const currentClassroom = useAppSelector(selectCurrentClassroom)
  const { isPausedByTeacher } = useContext(QuestionnaireContext)
  const formulaSheet = useFormulaSheet()
  const isMath = Cookies.get('isMath') === 'true'

  /**
   * Formula sheets match Assessment V1 availability: math assessments for
   * grades that have an existing grade-specific PDF can open the resource.
   */
  const showFormulaSheet = isMath && Boolean(formulaSheet)

  /**
   * Open the grade-specific formula sheet in a separate browser tab so the
   * student's current assessment state remains uninterrupted.
   */
  const openFormulaSheet = () => {
    window.open(formulaSheet)
  }

  /**
   * StudentTools already supports per-tool visibility props, so the footer
   * resolves classroom-wide and assessment/assignment-specific `other_tools`
   * permissions here and passes the final visibility down through those
   * existing props.
   */
  const isClassroomOtherToolsAllowed = currentClassroom?.other_tools === true
  const isAssignmentToolsScope =
    props.type === 'assignments' || props.type === 'preview-assignment'
  const isScopedOtherToolsAllowed = isAssignmentToolsScope
    ? other_tools === true
    : Cookies.get('other_tools') === 'true'
  const allowOtherTools =
    isClassroomOtherToolsAllowed && isScopedOtherToolsAllowed
  const studentToolsVisibilityProps = {
    markerVisible: allowOtherTools,
    highlighterVisible: allowOtherTools,
    contrastVisible: allowOtherTools
  }

  function toggleAnswersVisibility() {
    const nextSearchParams = new URLSearchParams(searchParams)

    if (showAnswers) {
      nextSearchParams.set('showAnswers', 'false')
    } else {
      nextSearchParams.delete('showAnswers')
    }

    setLocalSearchParams(nextSearchParams)
  }

  if (props.type === 'assignments') {
    return (
      <FooterContainer>
        <StudentTools {...studentToolsVisibilityProps} />
        <div />
        <FooterActionsContainer>
          <>
            <ActionButton
              label={'Clear'}
              variant={'clear'}
              width={67}
              onClick={props.onClear}
              disabled={isPausedByTeacher || props.disableClear}
            />

            <ActionButton
              label='Pause/Exit'
              variant='error'
              width={108}
              onClick={props.onPauseExit}
              // disabled={props.disablePauseExit}
            />

            <ActionButton
              label='Back'
              variant='info-dark'
              width={61}
              disabled={isPausedByTeacher || props.disableBack}
              onClick={props.onBack}
            />
            <ActionButton
              label='Next'
              variant='info'
              width={61}
              onClick={props.onNext}
              disabled={isPausedByTeacher || props.disableNext}
            />
            <ActionButton
              label='Finish'
              variant='success'
              width={70}
              onClick={props.onFinish}
              disabled={isPausedByTeacher || props.disableFinish}
            />
          </>
        </FooterActionsContainer>
      </FooterContainer>
    )
  }

  if (props.type === 'preview-assignment') {
    return (
      <FooterContainer>
        <StudentTools {...studentToolsVisibilityProps} />

        <div />

        <FooterActionsContainer>
          {!enableStudentSubmissionView && (
            <ActionButton
              label={showAnswers ? 'Hide Answers' : 'Show Answers'}
              variant={'clear'}
              width={128}
              onClick={toggleAnswersVisibility}
            />
          )}
          <ActionButton
            label='Back'
            variant='info-dark'
            width={61}
            disabled={props.disableBack}
            onClick={props.onBack}
          />
          <ActionButton
            label='Next'
            variant='info'
            width={61}
            onClick={props.onNext}
            disabled={props.disableNext}
          />
        </FooterActionsContainer>
      </FooterContainer>
    )
  }

  if (props.type === 'preview-assessment') {
    return (
      <FooterContainer>
        <StudentTools {...studentToolsVisibilityProps} />

        <FooterActionsContainer>
          {!enableStudentSubmissionView && (
            <ActionButton
              label={showAnswers ? 'Hide Answers' : 'Show Answers'}
              variant={'clear'}
              width={128}
              onClick={toggleAnswersVisibility}
            />
          )}
          <ActionButton
            label='Back'
            variant='info-dark'
            width={61}
            disabled={props.disableBack}
            onClick={props.onBack}
          />
          <ActionButton
            label='Next'
            variant='info'
            width={61}
            onClick={props.onNext}
            disabled={props.disableNext}
          />
        </FooterActionsContainer>
      </FooterContainer>
    )
  }

  if (props.type === 'assessments') {
    return (
      <FooterContainer>
        <span className='flex'>
          <StudentTools {...studentToolsVisibilityProps} notesVisible={false} />
        </span>

        <FooterActionsContainer>
          {showFormulaSheet ? (
            <ActionButton
              label='Formula Sheet'
              startIcon={<img src={OpenLink} alt='' width={16} height={16} />}
              variant='clear'
              width={160}
              onClick={openFormulaSheet}
            />
          ) : null}
          {props.readOnly ? null : (
            <>
              <ActionButton
                label='Clear'
                variant='clear'
                width={67}
                onClick={props.onClear}
                disabled={props.disableClear}
              />
              <ActionButton
                label='Pause/Exit'
                variant='error'
                width={108}
                onClick={props.onPauseExit}
                // disabled={props.disablePauseExit}
              />
            </>
          )}
          {props.readOnly ? (
            /**
             * Read only benchmark review only exposes navigation plus exit so
             * students cannot clear, finish, or open the pause flow.
             */
            <ActionButton
              label='Exit'
              variant='error'
              width={61}
              onClick={props.onPauseExit}
            />
          ) : null}
          <ActionButton
            label='Back'
            variant='info-dark'
            width={61}
            disabled={props.disableBack}
            onClick={props.onBack}
          />
          <ActionButton
            label='Next'
            variant='info'
            width={61}
            onClick={props.onNext}
            disabled={props.disableNext}
          />
          {props.readOnly ? null : (
            <ActionButton
              label='Finish'
              variant='success'
              disabled={props.disableFinish}
              width={70}
              onClick={props.onFinish}
            />
          )}
        </FooterActionsContainer>
      </FooterContainer>
    )
  }
}

export default Footer
